class FacetsForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    this.facetForm = this.querySelector('form');
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetsForm.searchParamsInitial;
      if (searchParams === FacetsForm.searchParamsPrev) return;
      FacetsForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    if (FacetsForm.countAbortController) {
      FacetsForm.countAbortController.abort();
      FacetsForm.countAbortController = null;
    }
    FacetsForm.searchParamsPrev = searchParams;
    const sections = FacetsForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facets-filter-form .loading__spinner, facets-sort-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetsForm.filterData.some(filterDataUrl)
        ? FacetsForm.renderSectionFromCache(filterDataUrl, event)
        : FacetsForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetsForm.updateURLHash(searchParams);

    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetsForm.filterData = [...FacetsForm.filterData, { html, url }];
        FacetsForm.renderFilters(html, event);
        FacetsForm.renderProductGridContainer(html);
        FacetsForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetsForm.filterData.find(filterDataUrl).html;
    FacetsForm.renderFilters(html, event);
    FacetsForm.renderProductGridContainer(html);
    FacetsForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static async renderProductCountFromSection(searchParams, targetRoot = document) {
    const url = `${window.location.pathname}?section_id=facets-product-count&${searchParams}`;
    const countNodes = targetRoot.querySelectorAll('[data-product-count]');
    if (!countNodes.length) return;

    if (FacetsForm.countAbortController) FacetsForm.countAbortController.abort();
    const controller = new AbortController();
    FacetsForm.countAbortController = controller;

    const loadingParents = new Set();
    countNodes.forEach((node) => {
      if (node.parentElement) {
        node.parentElement.setAttribute('data-loading', '');
        loadingParents.add(node.parentElement);
      }
    });

    try {
      const cached = FacetsForm.countCache.get(url);
      let html;
      if (cached) {
        html = cached;
      } else {
        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) throw new Error(`Count fetch failed: ${response.status}`);
        html = await response.text();
        FacetsForm.cacheCountResponse(url, html);
      }

      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const sourceNode = parsed.querySelector('[data-product-count]');
      if (!sourceNode) throw new Error('Count fetch: missing [data-product-count] in response');

      const count = sourceNode.textContent;
      countNodes.forEach((node) => {
        node.textContent = count;
      });
    } catch (err) {
      if (err.name !== 'AbortError') console.error(err);
    } finally {
      if (!controller.signal.aborted) {
        loadingParents.forEach((parent) => parent.removeAttribute('data-loading'));
      }
    }
  }

  static cacheCountResponse(url, html) {
    if (FacetsForm.countCache.size >= FacetsForm.COUNT_CACHE_MAX) {
      const oldest = FacetsForm.countCache.keys().next().value;
      FacetsForm.countCache.delete(oldest);
    }
    FacetsForm.countCache.set(url, html);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html) {
    if (document.getElementById('ProductCount')) {
      const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML;
      const container = document.getElementById('ProductCount');
      const containerDesktop = document.getElementById('ProductCountDesktop');
      container.innerHTML = count;
      container.classList.remove('loading');
      if (containerDesktop) {
        containerDesktop.innerHTML = count;
        containerDesktop.classList.remove('loading');
      }
      const loadingSpinners = document.querySelectorAll(
        '.facets-container .loading__spinner, facets-filter-form .loading__spinner, facets-sort-form .loading__spinner'
      );
      loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
    }
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetsForm .js-filter, #FacetsFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetsForm .js-filter, #FacetsFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetsForm.renderActiveFacets(parsedHTML);
    FacetsForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetsForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetsForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('facets__details')
          ? `.facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetsForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const elementSelectors = ['.facets__open', '.facets__count', '.sorting'];

    elementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    const sortLabelSrc = html.querySelector('.facets__sort .facets__open-wrapper');
    const sortLabelDom = document.querySelector('.facets__sort .facets__open-wrapper');
    if (sortLabelSrc && sortLabelDom) sortLabelDom.innerHTML = sortLabelSrc.innerHTML;

    const filtersForm = document.getElementById('FacetsForm');
    if (filtersForm) filtersForm.closest('facets-filter').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.facets__list');
    const sourceFacetsList = source.querySelector('.facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetsForm.renderPage(searchParams, event);
  }

  collectAllFormParams() {
    const params = [];
    [document.getElementById('FacetsForm'), document.getElementById('FacetSortForm')].forEach((form) => {
      if (!form) return;
      const formParams = this.createSearchParams(form);
      if (formParams) params.push(formParams);
    });
    return params.join('&');
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetsForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetsForm.renderPage(url);
  }
}

FacetsForm.filterData = [];
FacetsForm.searchParamsInitial = window.location.search.slice(1);
FacetsForm.searchParamsPrev = window.location.search.slice(1);
FacetsForm.countCache = new Map();
FacetsForm.COUNT_CACHE_MAX = 20;
FacetsForm.countAbortController = null;
FacetsForm.setListeners();

class FacetsFilterForm extends FacetsForm {
  constructor() {
    super();
    this.disclosure = this.closest('details.facets__disclosure');

    const applyButton = this.facetForm.querySelector('button');
    if (applyButton) applyButton.addEventListener('submit', this.debouncedOnSubmit.bind(this));

    if (this.disclosure) {
      this.disclosure.querySelector('summary').addEventListener('click', this.debouncedOnSubmit.bind(this));
    }

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);

    this.debouncedRefreshCount = debounce(() => {
      const params = this.createSearchParams(this.facetForm);
      FacetsForm.renderProductCountFromSection(params, this.facetForm);
    }, 300);
    this.facetForm.addEventListener('change', this.debouncedRefreshCount);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    if (this.disclosure && this.disclosure.hasAttribute('open')) return;

    this.onSubmitForm(this.collectAllFormParams(), event);
  }
}

customElements.define('facets-filter-form', FacetsFilterForm);

class FacetsSortForm extends FacetsForm {
  constructor() {
    super();
    this.handleSortClickOutside = this.handleSortClickOutside.bind(this);
    this.handleListboxKeydown = this.handleListboxKeydown.bind(this);
    this.handleListboxClick = this.handleListboxClick.bind(this);
    this.handleToggle = this.handleToggle.bind(this);

    this.sortDetails = this.querySelector('details.facets__sort');
    this.sortSummary = this.querySelector('summary.facets__sort-summary');
    this.sortListbox = this.querySelector('[role="listbox"]');
    this.hiddenInput = this.facetForm.querySelector('input[type="hidden"][name="sort_by"]');

    this.sortDetails.addEventListener('toggle', this.handleToggle);
    this.sortListbox.addEventListener('keydown', this.handleListboxKeydown);
    this.sortListbox.addEventListener('click', this.handleListboxClick);
    document.addEventListener('click', this.handleSortClickOutside);
  }

  connectedCallback() {
    if (!FacetsSortForm.restoreFocusOnConnect) return;
    FacetsSortForm.clearFocusRestore();

    const stolen = document.activeElement === document.body || document.activeElement === null;
    if (!stolen) return;

    requestAnimationFrame(() => {
      if (this.isConnected && this.sortSummary) this.sortSummary.focus();
    });
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.handleSortClickOutside);
  }

  getOptions() {
    return Array.from(this.sortListbox.querySelectorAll('[role="option"]'));
  }

  getSelectedOption() {
    return this.sortListbox.querySelector('[role="option"][aria-selected="true"]');
  }

  handleToggle() {
    const isOpen = this.sortDetails.hasAttribute('open');
    this.sortSummary.setAttribute('aria-expanded', String(isOpen));

    if (isOpen) {
      const selected = this.getSelectedOption() || this.getOptions()[0];
      if (selected) selected.focus();
    }
  }

  handleListboxKeydown(event) {
    const options = this.getOptions();
    if (!options.length) return;
    const currentIndex = options.indexOf(document.activeElement);

    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowRight': {
        event.preventDefault();
        const next = options[(currentIndex + 1 + options.length) % options.length];
        this.focusOption(next);
        break;
      }
      case 'ArrowUp':
      case 'ArrowLeft': {
        event.preventDefault();
        const prev = options[(currentIndex - 1 + options.length) % options.length];
        this.focusOption(prev);
        break;
      }
      case 'Home': {
        event.preventDefault();
        this.focusOption(options[0]);
        break;
      }
      case 'End': {
        event.preventDefault();
        this.focusOption(options[options.length - 1]);
        break;
      }
      case ' ':
      case 'Enter': {
        event.preventDefault();
        const focused = options[currentIndex];
        if (focused) this.commit(focused);
        break;
      }
      case 'Escape': {
        event.preventDefault();
        this.closePopup({ restoreFocus: true });
        break;
      }
      case 'Tab': {
        this.closePopup({ restoreFocus: false });
        break;
      }
    }
  }

  handleListboxClick(event) {
    const option = event.target.closest('[role="option"]');
    if (!option || !this.sortListbox.contains(option)) return;
    event.preventDefault();
    this.commit(option);
  }

  focusOption(option) {
    this.getOptions().forEach((o) => {
      o.setAttribute('tabindex', o === option ? '0' : '-1');
    });
    option.focus();
  }

  commit(option) {
    const value = option.getAttribute('data-value');
    if (value === null) return;

    const previousValue = this.hiddenInput.value;
    if (value !== previousValue) {
      this.hiddenInput.value = value;
      this.updateSelectionAria(option);
      FacetsSortForm.armFocusRestore();
      try {
        this.onSubmitForm(this.collectAllFormParams(), null);
      } catch (err) {
        FacetsSortForm.clearFocusRestore();
        throw err;
      }
      this.closePopup({ restoreFocus: false });
      return;
    }
    this.closePopup({ restoreFocus: true });
  }

  static armFocusRestore() {
    FacetsSortForm.clearFocusRestore();
    FacetsSortForm.restoreFocusOnConnect = true;

    const cancel = () => FacetsSortForm.clearFocusRestore();
    document.addEventListener('keydown', cancel, { once: true, capture: true });
    document.addEventListener('pointerdown', cancel, { once: true, capture: true });
    FacetsSortForm._cancelFocusRestore = cancel;

    FacetsSortForm._focusRestoreTimer = setTimeout(() => {
      FacetsSortForm.clearFocusRestore();
    }, 5000);
  }

  static clearFocusRestore() {
    FacetsSortForm.restoreFocusOnConnect = false;
    if (FacetsSortForm._cancelFocusRestore) {
      document.removeEventListener('keydown', FacetsSortForm._cancelFocusRestore, { capture: true });
      document.removeEventListener('pointerdown', FacetsSortForm._cancelFocusRestore, { capture: true });
      FacetsSortForm._cancelFocusRestore = null;
    }
    if (FacetsSortForm._focusRestoreTimer) {
      clearTimeout(FacetsSortForm._focusRestoreTimer);
      FacetsSortForm._focusRestoreTimer = null;
    }
  }

  updateSelectionAria(selectedOption) {
    this.getOptions().forEach((o) => {
      const isSelected = o === selectedOption;
      o.setAttribute('aria-selected', String(isSelected));
      o.setAttribute('tabindex', isSelected ? '0' : '-1');
    });
  }

  closePopup({ restoreFocus }) {
    if (!this.sortDetails.hasAttribute('open')) return;
    this.sortDetails.removeAttribute('open');
    if (restoreFocus && this.sortSummary) this.sortSummary.focus();
  }

  handleSortClickOutside(event) {
    if (this.sortDetails.hasAttribute('open') && !this.sortDetails.contains(event.target)) {
      this.sortDetails.removeAttribute('open');
    }
  }

  onSubmitHandler(event) {
    if (event) event.preventDefault();
    this.onSubmitForm(this.collectAllFormParams(), event);
    this.closePopup({ restoreFocus: false });
  }
}

FacetsSortForm.restoreFocusOnConnect = false;
FacetsSortForm._cancelFocusRestore = null;
FacetsSortForm._focusRestoreTimer = null;
customElements.define('facets-sort-form', FacetsSortForm);

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facets-filter-form') || document.querySelector('facets-filter-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
